import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiCheck, FiX, FiEdit3 } from 'react-icons/fi';
import { actOnWorkflow } from '../../api/workflows';
import { extractErrorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import Button from './Button';
import Modal from './Modal';
import { Textarea } from './Input';
import FormField from './FormField';

const ACTIONS = {
  APPROVED: { label: 'Approve', variant: 'success', icon: FiCheck, needsComment: false },
  REJECTED: { label: 'Reject', variant: 'danger', icon: FiX, needsComment: true },
  REVISION_REQUESTED: {
    label: 'Request Revision',
    variant: 'accent',
    icon: FiEdit3,
    needsComment: true,
  },
};

/**
 * <WorkflowActions workflow={...} onActed={(w) => ...} invalidateKeys={[...]} />
 * - workflow: WorkflowItem { id, currentApproverId, workflowStatus }
 * - invalidateKeys: React Query keys to invalidate on success.
 */
export default function WorkflowActions({
  workflow,
  invalidateKeys = [],
  onActed,
  showFor = 'currentApprover', // 'currentApprover' | 'admin' | 'always'
}) {
  const qc = useQueryClient();
  const { user, isSuperadmin, isAdmin } = useAuth();
  const [active, setActive] = useState(null); // 'APPROVED' | 'REJECTED' | 'REVISION_REQUESTED'
  const [comment, setComment] = useState('');

  const mutation = useMutation({
    mutationFn: ({ id, action, comment }) => actOnWorkflow(id, { action, comment }),
    onSuccess: (data) => {
      toast.success('Action recorded');
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      qc.invalidateQueries({ queryKey: ['workflows'] });
      qc.invalidateQueries({ queryKey: ['approvals'] });
      onActed?.(data);
      setActive(null);
      setComment('');
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  if (!workflow) return null;

  const terminal = ['APPROVED', 'REJECTED', 'CANCELLED'].includes(workflow.workflowStatus);
  if (terminal) return null;

  const isCurrentApprover =
    user?.id && workflow.currentApproverId && Number(user.id) === Number(workflow.currentApproverId);

  let visible = false;
  if (showFor === 'always') visible = true;
  else if (showFor === 'admin') visible = isAdmin;
  else visible = isCurrentApprover || isSuperadmin;

  if (!visible) return null;

  const cfg = active ? ACTIONS[active] : null;

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="success"
        icon={FiCheck}
        size="sm"
        onClick={() => setActive('APPROVED')}
      >
        Approve
      </Button>
      <Button
        variant="accent"
        icon={FiEdit3}
        size="sm"
        onClick={() => setActive('REVISION_REQUESTED')}
      >
        Revise
      </Button>
      <Button
        variant="danger"
        icon={FiX}
        size="sm"
        onClick={() => setActive('REJECTED')}
      >
        Reject
      </Button>

      <Modal
        open={!!active}
        onClose={() => {
          setActive(null);
          setComment('');
        }}
        title={cfg ? cfg.label : ''}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setActive(null);
                setComment('');
              }}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant={cfg?.variant || 'primary'}
              loading={mutation.isPending}
              onClick={() => {
                if (cfg?.needsComment && !comment.trim()) {
                  toast.error('A comment is required');
                  return;
                }
                mutation.mutate({
                  id: workflow.id,
                  action: active,
                  comment: comment.trim(),
                });
              }}
            >
              {cfg?.label || 'Submit'}
            </Button>
          </>
        }
      >
        <FormField
          label="Comment"
          required={cfg?.needsComment}
          hint={
            cfg?.needsComment
              ? 'Required — visible to the requester.'
              : 'Optional note attached to this action.'
          }
        >
          <Textarea
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Leave a note..."
          />
        </FormField>
      </Modal>
    </div>
  );
}
